import { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { OrderSettingsProvider, useOrderSettings } from 'components/dme/order/order-settings/useOrderSettings';
import routes from '../../constants/routes';
import Button from 'components/atoms/Button';
import Toast from 'components/atoms/Toast';
import OrderSettings from 'components/lab/fax/createOrder/OrderSettings';
import { newPatientInHistorySelector, patientHistoryReset } from 'store/slice/patientSlice';
import { useAttachIncomingDocumentMutation, useCreateLabOrderMutation } from 'store/services/labOrder';
import * as generalHelper from 'helpers/generalHelper';
import { FaxType, IncomingDocumentType, IncomingDocumentSource } from '../types'
import PatientInformation from 'components/lab/patient/PatientInformation';
import AIRxRecognitionCheckBox from 'components/dme/order/AIRxRecognitionCheckBox';

const DOB_FORMATS = ['MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', moment.ISO_8601]

interface IProps {
  currentFax: IncomingDocumentType;
  rotation?: number;
}

const NewPatientOrder = ({currentFax, rotation = 0}: IProps) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch();
  const [submitting, setSubmitting] = useState(false)
  const [officeNotesInRx, setOfficeNotesInRx] = useState(currentFax?.faxType === FaxType.RX_OFFICE_NOTES)
  const [rxRecognitionOrderInput, setRxRecognitionOrderInput] = useState(false)

  const { state: orderSettings } = useOrderSettings()
  const { officeId, ...newPatient } = useAppSelector(newPatientInHistorySelector) || {}

  const [createOrder] = useCreateLabOrderMutation()
  const [attachIncomingDocument] = useAttachIncomingDocumentMutation()

  const attachToOrder = (orderId: number, faxUuid: string) => {
    return attachIncomingDocument({
      orderId,
      faxUuid,
      uploadTypes: officeNotesInRx ? [FaxType.RX, FaxType.OFFICE_NOTES] : [FaxType.RX],
      fileSource: currentFax.source as IncomingDocumentSource,
      rotation: rotation || undefined,
      removeAfterAttach: true,
    })
  }

  const patientDob = moment(newPatient?.dob, DOB_FORMATS, true)
  const patientOverEighteen = patientDob.isValid() && moment().diff(patientDob, 'years') >= 18
  const isReadyToSubmit =
    orderSettings.isValid && newPatient && (patientOverEighteen || orderSettings.testType !== 'HST')
  const isHstPsg = orderSettings?.testType === 'HST' || orderSettings?.testType === 'PSG'

  const onSubmit = async () => {
    try {
      if (newPatient && !submitting) {
        setSubmitting(true)
        const data = {
          patient: {
            ...newPatient,
            dob: patientDob.format('YYYY-MM-DD'),
          },
          tests: Object.values(orderSettings.tests || []).map((conditions, idx) => ({
            ...conditions,
            night: idx + 1,
          })),
          testType: orderSettings.testType,
          officeId,
          rxRecognitionOrderInput,
        }
        const { data: orderCreated } = await createOrder(data)

        await attachToOrder(orderCreated.id, currentFax.uuid);

        Toast({ type: 'success', label: 'New order created!' })

        dispatch(patientHistoryReset());

        navigate(`${routes.index}${routes.order.edit}${orderCreated.id}?bucket=OPEN`)
      }
    } catch (error: any) {
      console.log(`Error submitting new order and patient info: ${error.message}`)
      Toast({
        type: 'error',
        label: `Error creating new Patient: ${error.message}`,
      })
    }
    setSubmitting(false)
  }

  return (
      <div>
        <PatientInformation patientData={newPatient} />
        <OrderSettings
          officeId={officeId}
          officeNotesInRx={officeNotesInRx}
          setOfficeNotesInRx={setOfficeNotesInRx}
        />
        {!isHstPsg &&
          <div className="d-flex align-items-center justify-content-between mx-2">
            <div className="ml-3 px-2">
              <AIRxRecognitionCheckBox
                rxRecognitionOrderInput={rxRecognitionOrderInput}
                setRxRecognitionOrderInput={setRxRecognitionOrderInput}
                containerClasses={'block'}
              />
            </div>
          </div>
        }
        <div className="d-flex flex-column align-items-end">
          <Button
            className="btn btn-danger float-right mr-2 mt-3 text-nowrap"
            onClick={onSubmit}
            disabled={!isReadyToSubmit || submitting}
            label={generalHelper.t('Create Order')}
          />
          {!patientOverEighteen && orderSettings.testType === 'HST' && (
            <div className="row mt-2">
              <div className="col-sm-12 d-flex justify-content-end">
                <span className="font-weight-bold text-danger">
                  Cannot order HST if patient is under 18
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
  )
}

const NewPatientOrderWrapper = ({currentFax, rotation}: IProps) => {
  return (
    <OrderSettingsProvider>
      <NewPatientOrder currentFax={currentFax} rotation={rotation}/>
    </OrderSettingsProvider>
  )
}

export default NewPatientOrderWrapper
